import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import GenericDataGrid from '../../../components/genericTable';
import StatusChip from '../../../components/statusChip';
import NotificationSnackbar from '../../../components/notificationSnackbar';
import { createManagementTableSx } from '../../../styles/tableStyles';
import {
    managementCreateButtonSx,
    managementHeaderActionSx,
    managementHeaderContentSx,
    managementHeaderSx,
    managementPageSubtitleSx,
} from '../../../styles/managementStyles';
import { buttonContentSx } from '../../../styles/dialogStyles';
import { compactTableActionButtonSx, tableDeleteButtonSx, tableViewButtonSx } from '../../../styles/actionStyles';
import DuplicateVersionModal from './duplicateVersionModal';
import { questionnaireVersionPath } from '../ekScoreConfiguration/ekScoreConfiguration.paths';
import {
    VERSION_STATUS,
    compareVersions,
    deleteVersion,
    getVersions,
    publishVersion,
} from './questionnaire.service';

const textCellSx = {
    fontSize: 16,
    lineHeight: '24px',
    letterSpacing: '0.15px',
    color: '#1C1B1F',
};

function formatDate(value) {
    if (!value) return '-';
    return new Date(value).toLocaleDateString(undefined, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    });
}

/**
 * eK Score questionnaire catalogue versions.
 *
 * A published version is immutable: an assessment stores the catalogue version it was scored
 * against, so changing its questions afterwards would silently rewrite the meaning of scores
 * already given. Editing therefore always goes through a duplicate — "upgrade" here means copy
 * the current version to a new draft, edit the draft, publish it, and the outgoing version is
 * retired rather than deleted so old assessments stay resolvable.
 */
function Questionnaire() {
    const navigate = useNavigate();
    const [versions, setVersions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [duplicateOpen, setDuplicateOpen] = useState(false);
    const [duplicateBase, setDuplicateBase] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });
    const closeSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));

    const load = useCallback(async () => {
        setLoading(true);
        const result = await getVersions();
        setVersions(result.success ? result.data : []);
        setLoading(false);
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    // Newest first: the version an admin is most likely to act on is the one at the top.
    const sorted = useMemo(
        () => [...versions].sort((a, b) => compareVersions(b.version, a.version)),
        [versions]
    );

    const currentVersion = useMemo(
        () => sorted.find((entry) => entry.status === VERSION_STATUS.CURRENT) || sorted[0],
        [sorted]
    );

    const openDuplicate = (base) => {
        setDuplicateBase(base);
        setDuplicateOpen(true);
    };

    const handleDuplicated = async (draft) => {
        setDuplicateOpen(false);
        await load();
        notify(`${draft.version} created from ${draft.basedOn}. Edit it, then publish.`);
        navigate(questionnaireVersionPath(draft.version));
    };

    const handlePublish = async (version) => {
        const result = await publishVersion(version);
        if (!result.success) {
            notify(result.error.message, 'error');
            return;
        }
        await load();
        notify(`${version} is now the current catalogue.`);
    };

    const handleDelete = async (version) => {
        const result = await deleteVersion(version);
        if (!result.success) {
            notify(result.error.message, 'error');
            return;
        }
        await load();
        notify(`Draft ${version} deleted.`);
    };

    const columns = [
        {
            field: 'version',
            headerName: 'Version',
            flex: 0.8,
            minWidth: 120,
            sortable: false,
            renderCell: (params) => (
                <Typography sx={{ ...textCellSx, fontWeight: 600 }}>{params.value}</Typography>
            ),
        },
        {
            field: 'status',
            headerName: 'Status',
            flex: 0.9,
            minWidth: 150,
            sortable: false,
            renderCell: (params) => (
                <StatusChip
                    status={params.value === VERSION_STATUS.CURRENT ? 'ACTIVE' : params.value}
                    labelOverride={
                        params.value === VERSION_STATUS.CURRENT
                            ? 'Current'
                            : params.value === VERSION_STATUS.DRAFT
                                ? 'Draft'
                                : 'Retired'
                    }
                />
            ),
        },
        {
            field: 'categories',
            headerName: 'Categories',
            flex: 0.7,
            minWidth: 110,
            sortable: false,
            renderCell: (params) => (
                <Typography sx={textCellSx}>{params.row.categoryCount ?? 0}</Typography>
            ),
        },
        {
            field: 'questions',
            headerName: 'Questions',
            flex: 0.7,
            minWidth: 110,
            sortable: false,
            renderCell: (params) => (
                <Typography sx={textCellSx}>{params.row.questionCount ?? 0}</Typography>
            ),
        },
        {
            field: 'answers',
            headerName: 'Answers',
            flex: 0.7,
            minWidth: 100,
            sortable: false,
            renderCell: (params) => (
                <Typography sx={textCellSx}>{params.row.optionCount ?? 0}</Typography>
            ),
        },
        {
            field: 'basedOn',
            headerName: 'Based on',
            flex: 0.7,
            minWidth: 110,
            sortable: false,
            renderCell: (params) => <Typography sx={textCellSx}>{params.value || '-'}</Typography>,
        },
        {
            field: 'publishedAt',
            headerName: 'Published',
            flex: 0.9,
            minWidth: 140,
            sortable: false,
            renderCell: (params) => <Typography sx={textCellSx}>{formatDate(params.value)}</Typography>,
        },
        {
            field: 'actions',
            headerName: 'Actions',
            flex: 1.6,
            minWidth: 330,
            sortable: false,
            renderCell: (params) => {
                const row = params.row;
                const isDraft = row.status === VERSION_STATUS.DRAFT;
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
                        <Button
                            sx={{ ...tableViewButtonSx, ...compactTableActionButtonSx }}
                            onClick={() => navigate(questionnaireVersionPath(row.version))}
                        >
                            {isDraft ? 'Edit' : 'View'}
                        </Button>
                        <Button
                            sx={{ ...tableViewButtonSx, ...compactTableActionButtonSx }}
                            onClick={() => openDuplicate(row)}
                        >
                            Duplicate
                        </Button>
                        {isDraft && (
                            <>
                                <Button
                                    sx={{ ...tableViewButtonSx, ...compactTableActionButtonSx, bgcolor: '#EEF7E6', color: '#4F8506', '&:hover': { bgcolor: '#EEF7E6' } }}
                                    onClick={() => handlePublish(row.version)}
                                >
                                    Publish
                                </Button>
                                <Button
                                    sx={{ ...tableDeleteButtonSx, ...compactTableActionButtonSx }}
                                    onClick={() => handleDelete(row.version)}
                                >
                                    Delete
                                </Button>
                            </>
                        )}
                    </Box>
                );
            },
        },
    ];

    const pageRows = sorted.slice(currentPage * rowsPerPage, currentPage * rowsPerPage + rowsPerPage);

    return (
        <>
            <Box sx={managementHeaderSx}>
                <Box sx={managementHeaderContentSx}>
                    <Typography sx={managementPageSubtitleSx}>
                        eK Score questionnaire catalogue. A published version is never edited — duplicate it,
                        change the draft, then publish.
                    </Typography>
                </Box>
                <Box sx={managementHeaderActionSx}>
                    <Button
                        variant="contained"
                        disabled={!currentVersion}
                        onClick={() => openDuplicate(currentVersion)}
                        sx={{ ...managementCreateButtonSx, width: { xs: '100%', md: 'auto' }, minWidth: { xs: '100%', md: 204 } }}
                    >
                        <Box component="span" sx={buttonContentSx}>
                            <Icon icon="tabler:git-branch" width="18" height="18" />
                            <span>NEW VERSION</span>
                        </Box>
                    </Button>
                </Box>
            </Box>

            <Box sx={{ width: '100%' }}>
                <GenericDataGrid
                    rows={pageRows}
                    columns={columns}
                    getRowId={(row) => row.version}
                    rowCount={sorted.length}
                    page={currentPage}
                    pageSize={rowsPerPage}
                    loading={loading}
                    dataGridSx={createManagementTableSx()}
                    tableMinWidth={1240}
                    rowHeight={60}
                    onPaginationChange={(page, pageSize) => {
                        setCurrentPage(page);
                        setRowsPerPage(pageSize);
                    }}
                />
            </Box>

            <DuplicateVersionModal
                open={duplicateOpen}
                baseVersion={duplicateBase}
                existingVersions={versions}
                onClose={() => setDuplicateOpen(false)}
                onCreated={handleDuplicated}
            />

            <NotificationSnackbar
                open={snackbar.open}
                onClose={closeSnackbar}
                message={snackbar.message}
                severity={snackbar.severity}
            />
        </>
    );
}

export default Questionnaire;
